import { FC, useEffect, useMemo, useState, useCallback, useRef } from 'react'
import cn from 'classnames'
import s from './Searchbar.module.scss'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { ClickOutside } from '@corratech/pylot-utils'
import SearchAutocomplete from '../SearchAutocomplete'
import debounce from 'lodash.debounce'
import { Search } from '@components/icons'
import { Button } from '@corratech/pylot-ui'

const searchPageURL = `/search`
interface Props {
    className?: string
    id?: string
}

const Searchbar: FC<Props> = ({ className, id = 'search' }) => {
    const router = useRouter()
    const { t } = useTranslation('common')
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [showAutoComplete, setShowAutoComplete] = useState<boolean>(true)
    const closeSearchBar = () => {
        setShowAutoComplete(false)
    }
    useEffect(() => {
        router.prefetch(searchPageURL)

        router.events.on('routeChangeStart', closeSearchBar)

        // If the component is unmounted, unsubscribe
        // from the event with the `off` method:
        return () => {
            router.events.off('routeChangeStart', closeSearchBar)
        }
    }, [])

    const debounceSearchTerm = useCallback(
        debounce((value) => {
            setSearchTerm(value)
        }, 500),
        []
    )

    const ref = useRef() as React.MutableRefObject<HTMLInputElement>

    const getSearchVisibilityClass = () => {
        if (searchTerm.length > 2 && showAutoComplete)
            return s['search-auto-complete-visible']
        return s['search-auto-complete-hidden']
    }

    return useMemo(
        () => (
            <ClickOutside
                active={searchTerm.length > 2}
                onClick={() => setShowAutoComplete(false)}
            >
                <div className={cn(s['searchbar-main'], className)}>
                    <label htmlFor={id} className="sr-only">
                        {t('search')}
                    </label>
                    <input
                        id={id}
                        className={s.input}
                        ref={ref}
                        onChange={(e) => {
                            debounceSearchTerm(e.target.value.trim())
                            return debounceSearchTerm.cancel
                        }}
                        onFocus={(e) => {
                            setShowAutoComplete(true)
                            debounceSearchTerm(e.target.value.trim())
                            return debounceSearchTerm.cancel
                        }}
                        placeholder={t('Search Hookah Shisha')}
                        defaultValue={router.query.q}
                        autoComplete="off"
                        onKeyUp={(e) => {
                            e.preventDefault()
                            if (e.key === 'Enter') {
                                const q = e.currentTarget.value.trim()
                                q.length > 2 &&
                                    router.push({
                                        pathname: searchPageURL,
                                        query: q ? { q } : {}
                                    })
                                setShowAutoComplete(false)
                            }
                        }}
                    />
                    <div className={s['icon-container']}>
                        <Button
                            className={s['search-icon']}
                            variant="link"
                            disabled={searchTerm.length <= 2}
                            onClick={() => {
                                router.push({
                                    pathname: searchPageURL,
                                    query: searchTerm ? { q: searchTerm } : {}
                                })
                                setShowAutoComplete(false)
                            }}
                        >
                            <Search />
                        </Button>
                    </div>
                    {searchTerm.length > 2 && (
                        <div className={getSearchVisibilityClass()}>
                            <SearchAutocomplete
                                searchTerm={searchTerm}
                                searchPageURL={searchPageURL}
                            />
                        </div>
                    )}
                </div>
            </ClickOutside>
        ),
        [searchTerm, showAutoComplete]
    )
}

export default Searchbar
